import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from '../auth/AuthContext';
import { getAuthHeader } from '../../utils/auth';
import { extractEventIdFromResponse } from './hooks/KraKpiServices';
import { mapEventToObjective, fetchObjectiveWithDetails } from './hooks/ObjectiveService';
import { PositionResolutionService } from '../staff/services/PositionResolutionService';

const trackedEntityTypeId = 'OaAbpIs1cV7';
const programId = 'yiT00XKrrHP';
const PROGRAM_ID = 'yiT00XKrrHP';
const PROGRAM_STAGE_ID = 'VIBtpvkpHFR';

// In PerformanceContext.js - Update the mapping
export const dataElementMapping = {
  name: 'EuN5ztFnXsQ',
  description: 'nWqW5BrcgP1',
  weight: 'yfb9RCRpMB2',
  objectiveBaselineValue: 'WvfEOREFo9l',
  status: 'DL7KxNhiV0x',
  startDate: 'eDcVy661j9e',
  completionDate: 'J0rjxrDKn1l',
  objectiveId: 'plJovUlxfQ0',
  performanceYear: 'CVaNcskWvmN',
  remarks: 'KeGWxm1IDYF',
  approvalStatus: 'DzQorireRBf', // Add approval status mapping
  score: 'LEEqJmI9Lyi',
  kpiDescription: 'rQqF75V9t7K',
  kpiName: 'DBmylDQA9KX',
  kpiTarget: 'Ru68Ax2Xo2L',
  kraDescription: 'DEECWPTj42E',
  kraName: 'obapz6Gnyla',
  kraWeight: 'Xl4VEFoIFIw',
  objectiveAlignment: 'neuilg3L4d2',
  objectiveRoleAssigned: 'vFoMLF5eZ5X',
  companyAlignedGoal: 'VhrIQQIVpB9',
  workType: 'wX6yZ7aB8cD9',
};

const programAccessCache = new Map();
const PerformanceContext = createContext();

function PerformanceContextProvider({ children }) {
  const engine = useDataEngine();
  const { user } = useAuth();
  const positionService = new PositionResolutionService(engine);

  const [selectedHigherLevelObjective, setSelectedHigherLevelObjective] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [uiMode, setUiMode] = useState('list');
  const [editingLevelId, setEditingLevelId] = useState(null);
  const [editingObjective, setEditingObjective] = useState(null);
  const [data, setData] = useState([]);
  const [currentOrgUnit, setCurrentOrgUnit] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [expandedLevels, setExpandedLevels] = useState({});
  const [expandedObjectives, setExpandedObjectives] = useState({});
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    visible: false, type: '', title: '', details: ''
  });

  const onSaveCallback = useRef(null);
  const onErrorCallback = useRef(null);
  const dataElementToFieldMap = Object.entries(dataElementMapping).reduce((acc, [field, id]) => {
    acc[id] = field;
    return acc;
  }, {});

  const recentSaveIds = new Set();
  const MAX_RECENT_IDS = 100;
  const SAVE_ID_TIMEOUT = 30000;
  let saveCallCount = 0;

  // ==================== HELPER FUNCTIONS ====================
  const getKeyByValue = (object, value) => {
    return Object.keys(object).find(key => object[key] === value);
  };

  const showNotification = (type, title, details = '') => {
    setNotification({ visible: true, type, title, details });
    setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 5000);
  };

  // If you prefer function hoisting, use `function` declaration so it's available before use.
  async function enrollTeiWithUserOrgUnit(trackedEntityInstance, user) {
    if (!trackedEntityInstance) {
      throw new Error('trackedEntityInstance is required to create an enrollment');
    }
    const orgUnit = user?.organisationUnits?.[0]?.id;
    if (!orgUnit) {
      throw new Error('User orgUnit missing; cannot enroll TEI');
    }

    // Use the same program you use for events
    const program = 'yiT00XKrrHP';
    const today = new Date().toISOString().split('T')[0];

    const payload = {
      trackedEntityInstance,
      orgUnit,
      program,
      enrollmentDate: today,
      incidentDate: today,
      status: 'ACTIVE',
    };

    const res = await fetch('/api/enrollments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      console.error('[enrollTeiWithUserOrgUnit] enrollment API error', res.status, text);
      throw new Error(`Enrollment failed: HTTP ${res.status} ${res.statusText} - ${text}`);
    }

    // Parse response. DHIS2 responses vary depending on server version or proxy.
    // Try a few common spots to find the created enrollment id.
    const json = await res.json();

  
    const enrollmentId =
      json?.enrollment ||
      json?.response?.enrollment ||
      (json?.response?.importSummaries?.[0]?.reference) ||
      json?.reference ||
      null;

    if (!enrollmentId) {
      
      if (json && typeof json === 'object') {
        for (const key of ['enrollment', 'id', 'reference']) {
          if (json[key]) {
            return json[key];
          }
        }
        // deep search top-level response
        const maybe = json?.response?.importSummaries?.[0]?.reference;
        if (maybe) return maybe;
      }

      console.warn('[enrollTeiWithUserOrgUnit] Could not detect enrollment id in response:', json);
      // As a fallback, throw so caller knows enrollment failed to produce id
      throw new Error('Enrollment succeeded but enrollment id could not be determined from response.');
    }

    return enrollmentId;
  }


  const ensureProgramAssignment = async (engine, programId, orgUnitId) => {
    try {
      const programResponse = await engine.query({
        program: {
          resource: `programs/${programId}`,
          params: {
            fields: 'id,name,programType,organisationUnits[id,name,path],programStages[id]'
          }
        }
      });
      const program = programResponse.program;
      const alreadyAssigned = program.organisationUnits?.some(ou => ou.id === orgUnitId);
      if (!alreadyAssigned) {
        const updatedOrganisationUnits = [...(program.organisationUnits || []), { id: orgUnitId }];
        await engine.mutate({
          type: 'update',
          resource: `programs/${programId}`,
          data: {
            id: program.id,
            name: program.name,
            programType: program.programType,
            organisationUnits: updatedOrganisationUnits,
          }
        });
        console.log(`✅ Program ${programId} assigned to orgUnit ${orgUnitId}`);
      }
    } catch (err) {
      console.error("❌ Error ensuring program assignment", err);
      throw err;
    }
  };

  const verifyProgramAccess = async (orgUnitId) => {
    if (programAccessCache.has(orgUnitId)) {
      return programAccessCache.get(orgUnitId);
    }
    const response = await engine.query({
      ouPrograms: {
        resource: `organisationUnits/${orgUnitId}`,
        params: { fields: 'programs[id]' }
      }
    });
    const hasAccess = response.ouPrograms.programs?.some(p => p.id === programId) || false;
    programAccessCache.set(orgUnitId, hasAccess);
    return hasAccess;
  };

  const verifyUserOrgUnitAccess = async (orgUnitId) => {
    if (typeof orgUnitId !== 'string') {
      if (typeof orgUnitId === 'object' && orgUnitId !== null && typeof orgUnitId.id === 'string') {
        orgUnitId = orgUnitId.id;
      } else {
        throw new Error('orgUnitId must be a string or object with id string property');
      }
    }
    try {
      const response = await fetch(`/api/organisationUnits/${orgUnitId}?fields=id`, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) return response.status === 404 ? false : false;
      const data = await response.json();
      return !!data.id;
    } catch (error) {
      console.error('verifyUserOrgUnitAccess error:', error);
      return false;
    }
  };

  const getCurrentUser = async () => {
    try {
      const userStr = localStorage.getItem('currentUser');
      if (userStr) return JSON.parse(userStr);
      const response = await fetch(`/api/me`, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (response.ok) {
        const userData = await response.json();
        localStorage.setItem('currentUser', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('[getCurrentUser] Failed to get current user:', error);
    }
    return null;
  };

  // ==================== ENROLLMENT FUNCTIONS ====================
  const enrollTeiWithPositionOrgUnit = async (teiId, positionOrgUnit) => {
    const enrollmentsUrl = new URL('/api/enrollments', window.location.origin);
    enrollmentsUrl.searchParams.append('trackedEntityInstance', teiId);
    enrollmentsUrl.searchParams.append('program', programId);
    enrollmentsUrl.searchParams.append('fields', 'enrollment,status');
    enrollmentsUrl.searchParams.append('skipPaging', 'true');
    enrollmentsUrl.searchParams.append('ou', positionOrgUnit);

    try {
      const response = await fetch(enrollmentsUrl.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        credentials: 'include',
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed to fetch enrollments: ${response.status} - ${errText}`);
      }

      const data = await response.json();
      const activeEnrollment = data.enrollments?.find(e => e.status === 'ACTIVE');
      if (activeEnrollment) {
        return activeEnrollment.enrollment;
      }

      // Create new enrollment
      const createPayload = {
        trackedEntityInstance: teiId,
        program: programId,
        orgUnit: positionOrgUnit,
        enrollmentDate: new Date().toISOString().slice(0, 10),
        incidentDate: new Date().toISOString().slice(0, 10),
        status: 'ACTIVE'
      };

      const createResponse = await fetch('/api/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        credentials: 'include',
        body: JSON.stringify(createPayload)
      });

      if (!createResponse.ok) {
        const errText = await createResponse.text();
        throw new Error(`Failed to create enrollment: ${createResponse.status} - ${errText}`);
      }

      const createData = await createResponse.json();
      const newEnrollmentId = createData.response.importSummaries?.[0]?.reference;
      if (!newEnrollmentId) {
        throw new Error('Enrollment creation succeeded but no enrollment ID returned');
      }
      return newEnrollmentId;
    } catch (error) {
      console.error('[enrollTeiWithPositionOrgUnit] Enrollment process failed:', error);
      throw error;
    }
  };

  const checkTEIEnrollment = async (teiId) => {
    try {
      const response = await engine.query({
        enrollments: {
          resource: `trackedEntityInstances/${teiId}`,
          params: { fields: 'enrollments[program,status]' }
        }
      });
      const enrollments = response.enrollments?.enrollments || response.enrollments || [];
      return enrollments.some(e => e.program === programId && e.status === 'ACTIVE');
    } catch (error) {
      console.error('Error checking enrollments:', error);
      return false;
    }
  };

  const enrollPositionInProgram = async (teiId, orgUnitId) => {
    try {
      const response = await engine.mutate({
        resource: 'enrollments',
        type: 'create',
        data: {
          trackedEntityInstance: teiId,
          program: programId,
          orgUnit: orgUnitId,
          enrollmentDate: new Date().toISOString().split('T')[0],
          incidentDate: new Date().toISOString().split('T')[0]
        }
      });
      return response;
    } catch (error) {
      console.error('Error creating enrollment:', error);
      throw error;
    }
  };

  const loadHigherLevelObjectiveDetails = useCallback(async (objectiveEventId) => {
    if (!objectiveEventId) return null;

    try {
      const response = await engine.query({
        objective: {
          resource: `events/${objectiveEventId}`,
          params: {
            fields: 'event,orgUnit,storedBy,dataValues[dataElement,value]'
          }
        }
      });

      const dataValues = {};
      response.objective.dataValues?.forEach(dv => {
        dataValues[dv.dataElement] = dv.value;
      });

      const mappedObjective = {
        id: response.objective.event,
        name: dataValues[dataElementMapping.name] || 'Unnamed Objective',
        description: dataValues[dataElementMapping.description] || '',
        userRole: dataValues[dataElementMapping.objectiveRoleAssigned] || '',
        creatorRole: dataValues[dataElementMapping.objectiveRoleAssigned] || '',
        storedBy: response.objective.storedBy,
        orgUnit: response.objective.orgUnit,
        isLoading: false // mark as loaded
      };

      // Update state if this matches the selected objective
      setSelectedHigherLevelObjective(prev => {
        if (prev?.id === objectiveEventId) {
          return mappedObjective;
        }
        return prev;
      });

      return mappedObjective;
    } catch (error) {
      console.error('Failed to load higher-level objective details:', error);
      // Mark as deleted/unavailable
      setSelectedHigherLevelObjective(prev => {
        if (prev?.id === objectiveEventId) {
          return {
            ...prev,
            isDeleted: true,
            error: 'Could not load aligned objective.'
          };
        }
        return prev;
      });
      return null;
    }
  }, [engine, setSelectedHigherLevelObjective, dataElementMapping]);


  // ==================== POSITION RESOLUTION ====================
  const checkPositionTEIExists = async (staffUserId, orgUnitId) => {
    console.log("🔍 [POSITION DEBUG] checkPositionTEIExists called", { staffUserId, orgUnitId });

    try {
      if (!staffUserId || !orgUnitId) {
        throw new Error('staffUserId and orgUnitId are required');
      }

      // === STEP 1: Get root org unit ID from the provided orgUnitId ===
      let rootOrgUnitId = orgUnitId; // fallback to input if path fetch fails
      try {
        console.log("🔍 [POSITION DEBUG] Fetching org unit path for:", orgUnitId);
        const pathResponse = await engine.query({
          orgUnitPath: {
            resource: `organisationUnits/${orgUnitId}`,
            params: { fields: 'id,path' }
          }
        });

        const path = pathResponse.orgUnitPath.path;
        console.log("🔍 [POSITION DEBUG] Full org unit path:", path);

        // Safely extract root: works for "/A/B/C" and "A/B/C"
        const pathParts = path.split('/').filter(part => part.trim() !== '');
        if (pathParts.length > 0) {
          rootOrgUnitId = pathParts[0];
          console.log("✅ [POSITION DEBUG] Root org unit ID determined:", rootOrgUnitId);
        } else {
          console.warn("⚠️ [POSITION DEBUG] Empty path parts — using original orgUnitId as fallback");
        }
      } catch (pathError) {
        console.error("⚠️ [POSITION DEBUG] Failed to fetch org unit path — using orgUnitId as-is", pathError.message);
        // Keep rootOrgUnitId = orgUnitId (original behavior)
      }

      // === STEP 2: Try to find Position TEI under ROOT org unit + descendants ===
      let teis = [];
      try {
        console.log("🔍 [POSITION DEBUG] Searching for Position TEI under root org unit:", rootOrgUnitId);
        const response = await engine.query({
          trackedEntityInstances: {
            resource: 'trackedEntityInstances',
            params: {
              trackedEntityType: 'UGGo0Ebjl0E', 
              ou: rootOrgUnitId,
              ouMode: 'DESCENDANTS', // 
              filter: `V66IFsw5kq6:eq:${staffUserId}`, 
              fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
              pageSize: 1
            }
          }
        });
        teis = response.trackedEntityInstances?.trackedEntityInstances || [];
        console.log("🔍 [POSITION DEBUG] Found TEIs under root (DESCENDANTS):", teis.length);
      } catch (error) {
        console.warn('⚠️ [POSITION DEBUG] Position lookup under root org unit failed, trying global...', error.message);
        // Fallback: search globally (no org unit)
        try {
          const globalResponse = await engine.query({
            trackedEntityInstances: {
              resource: 'trackedEntityInstances',
              params: {
                trackedEntityType: 'UGGo0Ebjl0E',
                filter: `V66IFsw5kq6:eq:${staffUserId}`,
                fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
                pageSize: 1
              }
            }
          });
          teis = globalResponse.trackedEntityInstances?.trackedEntityInstances || [];
          console.log("🔍 [POSITION DEBUG] Found TEIs in global search:", teis.length);
        } catch (globalError) {
          console.error("❌ [POSITION DEBUG] Global search also failed:", globalError.message);
        }
      }

      // === STEP 3: Return result ===
      if (teis.length > 0) {
        console.log("✅ [POSITION DEBUG] Position TEI found:", teis[0].trackedEntityInstance, "in org unit:", teis[0].orgUnit);
        return { positionTEI: teis[0], rootOrgUnitId };
      }

      console.warn("⚠️ [POSITION DEBUG] No Position TEI found for staffUserId:", staffUserId);
      return { positionTEI: null, rootOrgUnitId };

    } catch (error) {
      console.error('❌ [POSITION TEI] Unexpected error in checkPositionTEIExists:', error);
      return null;
    }
  };

  // ==================== OBJECTIVE HANDLERS ====================
  const updateObjective = async (objectiveData) => {
    console.log('✅ UPDATE - Starting objective update:', objectiveData.id);
    try {
      // 🔹 STEP 1: Resolve position TEI (do NOT fall back to hardcoded values)
      const positionTEI = await checkPositionTEIExists(user.id, user.organisationUnits?.[0]?.id);
      if (!positionTEI) {
        throw new Error('Position not found for current user');
      }

      const trackedEntityInstance = positionTEI.trackedEntityInstance;
      const orgUnit = positionTEI.orgUnit;

      // 🔹 STEP 2: Get or create enrollment for the POSITION (not staff)
      let enrollment = objectiveData.enrollment;
      if (!enrollment) {
        // Try to fetch existing active enrollment
        const enrollmentsUrl = `/api/enrollments?trackedEntityInstance=${trackedEntityInstance}&program=${PROGRAM_ID}&fields=enrollment,status&ou=${orgUnit}`;
        const res = await fetch(enrollmentsUrl, { headers: getAuthHeader() });
        if (res.ok) {
          const data = await res.json();
          const active = data.enrollments?.find(e => e.status === 'ACTIVE');
          enrollment = active?.enrollment;
        }
        // If still no enrollment, create one
        if (!enrollment) {
          const createRes = await fetch('/api/enrollments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify({
              trackedEntityInstance,
              program: PROGRAM_ID,
              orgUnit,
              enrollmentDate: new Date().toISOString().slice(0, 10),
              incidentDate: new Date().toISOString().slice(0, 10),
              status: 'ACTIVE',
            })
          });
          const createData = await createRes.json();
          enrollment = createData.response?.importSummaries?.[0]?.reference;
          if (!enrollment) {
            throw new Error('Failed to create enrollment for position');
          }
        }
      }

      // 🔹 STEP 3: Build dataValues
      const dataValues = [
        { dataElement: dataElementMapping.name, value: objectiveData.name || '' },
        { dataElement: dataElementMapping.description, value: objectiveData.description || '' },
        { dataElement: dataElementMapping.weight, value: objectiveData.weight?.toString() || '0' },
        { dataElement: dataElementMapping.startDate, value: objectiveData.startDate || '' },
        { dataElement: dataElementMapping.completionDate, value: objectiveData.completionDate || '' },
        { dataElement: dataElementMapping.objectiveAlignment, value: objectiveData.objectiveAlignment || '' },
        { dataElement: dataElementMapping.objectiveRoleAssigned, value: objectiveData.objectiveRoleAssigned || '' },
        { dataElement: dataElementMapping.companyAlignedGoal, value: objectiveData.companyAlignedGoal || '' },
        { dataElement: dataElementMapping.performanceYear, value: objectiveData.performanceYear?.toString() || new Date().getFullYear().toString() }
      ].filter(dv => dv.value !== '' && dv.value !== undefined && dv.value !== null);

      // 🔹 STEP 4: Add Position TEI linkage (redundant but safe)
      dataValues.push({ dataElement: 'SlgO60cMK3F', value: trackedEntityInstance });

      // 🔹 STEP 5: Build event payload using POSITION context
      const eventData = {
        event: objectiveData.eventId || objectiveData.id,
        program: PROGRAM_ID,
        programStage: PROGRAM_STAGE_ID,
        orgUnit: orgUnit, // ← Position's org unit
        eventDate: objectiveData.startDate || new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        dataValues,
        trackedEntityInstance, // ← Position TEI
        enrollment
      };

      // 🔹 STEP 6: Send update
      const updateResponse = await fetch(`/api/events/${objectiveData.eventId || objectiveData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(eventData),
        credentials: 'include',
      });

      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        if (errorText.includes('already been deleted') || updateResponse.status === 404) {
          return await handleSaveObjective({ ...objectiveData, id: undefined, eventId: undefined });
        }
        throw new Error(`Failed to update event: ${updateResponse.status} - ${errorText}`);
      }

      const result = await updateResponse.json();
      return {
        id: result.response?.uid || objectiveData.id,
        eventId: result.response?.uid || objectiveData.id,
        orgUnit,
        trackedEntityInstance,
        enrollment,
        objectiveAlignment: objectiveData.objectiveAlignment,
        companyAlignedGoal: objectiveData.companyAlignedGoal,
      };
    } catch (error) {
      console.error('❌ UPDATE - Error updating objective event:', error);
      throw error;
    }
  };

  const handleSaveObjective = async (objective) => {
    const staffUserId = user?.id;
    if (!staffUserId) {
      throw new Error('User ID not found for current user');
    }

    const lookupOrgUnitId = user?.organisationUnits?.[0]?.id;
    if (!lookupOrgUnitId) {
      throw new Error('Organisation unit not selected');
    }

    const callId = ++saveCallCount;
    try {
      // ==================== STEP 1: Ensure Position TEI exists ====================
      let { positionTEI, rootOrgUnitId } = await checkPositionTEIExists(staffUserId, lookupOrgUnitId);

      // If still not found, auto-create it
      if (!positionTEI) {
        throw new Error('Position record not found. Please contact your administrator to assign you a position before creating objectives.');
      }

      // ==================== STEP 2: Ensure enrollment and get its ID ====================
      let enrollmentId = objective.enrollment || objective.enrollmentId;

      if (!enrollmentId) {
        // 🔑 CRITICAL FIX 1: Use ACCESSIBLE mode + NO org unit restriction
        const fetchExistingEnrollment = async () => {
          const enrollmentsUrl = `/api/enrollments?trackedEntityInstance=${positionTEI.trackedEntityInstance}&program=${programId}&fields=enrollment,status&ouMode=ACCESSIBLE`;
          try {
            const res = await fetch(enrollmentsUrl, {
              headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
              credentials: 'include',
            });
            if (res.ok) {
              const data = await res.json();
              // ✅ Return FIRST active enrollment (handles multiple edge cases)
              return data.enrollments?.find(e => e.status === 'ACTIVE')?.enrollment || null;
            }
            console.warn(`⚠️ Fetch enrollment failed (${res.status}). User may lack permissions.`);
            return null;
          } catch (err) {
            console.error('❌ Error fetching enrollments:', err);
            return null;
          }
        };

        // 🔑 CRITICAL FIX 2: Always fetch FIRST before attempting creation
        enrollmentId = await fetchExistingEnrollment();
        
        // Only create if truly missing (prevents duplicate attempts)
        if (!enrollmentId) {
          console.log('🔄 Creating NEW enrollment for Position TEI...');
          const enrollmentOrgUnit = user?.organisationUnits?.[0]?.id || positionTEI.orgUnit;
          
          try {
            const createRes = await fetch('/api/enrollments', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
              credentials: 'include',
              body: JSON.stringify({
                trackedEntityInstance: positionTEI.trackedEntityInstance,
                program: programId,
                orgUnit: enrollmentOrgUnit,
                enrollmentDate: new Date().toISOString().slice(0, 10),
                incidentDate: new Date().toISOString().slice(0, 10),
                status: 'ACTIVE',
              }),
            });

            // Parse response safely
            let createData;
            try {
              createData = await createRes.json();
            } catch {
              throw new Error(`Enrollment creation failed: ${createRes.status} ${await createRes.text()}`);
            }

            // 🔑 CRITICAL FIX 3: Handle DHIS2's multiple response formats
            enrollmentId = 
              createData.response?.importSummaries?.[0]?.reference || // Standard import
              createData.response?.enrollment ||                      // Direct response
              createData.enrollment ||                                // Top-level
              createData.reference;                                   // Fallback

            // 🔑 CRITICAL FIX 4: If ID missing BUT response was 201, fetch immediately
            if (!enrollmentId && createRes.status === 201) {
              console.log('⚠️ Enrollment ID missing in response. Fetching...');
              enrollmentId = await fetchExistingEnrollment();
            }

            // 🔑 CRITICAL FIX 5: Handle "already enrolled" gracefully
            if (!enrollmentId) {
              const errorMsg = 
                createData.message || 
                createData.response?.description ||
                createData.response?.importSummaries?.[0]?.description ||
                'Unknown error';
              
              if (errorMsg.includes('already has an active enrollment')) {
                console.log('🔄 Enrollment exists but not retrieved. Fetching...');
                enrollmentId = await fetchExistingEnrollment();
                if (!enrollmentId) {
                  throw new Error('Position already enrolled but ID inaccessible. Contact administrator.');
                }
              } else {
                throw new Error(`Enrollment creation failed: ${errorMsg}`);
              }
            }
          } catch (createErr) {
            // 🔑 CRITICAL FIX 6: Final fallback - retry fetch after ANY creation error
            console.log('⚠️ Creation failed. Checking for existing enrollment...');
            enrollmentId = await fetchExistingEnrollment();
            if (!enrollmentId) throw createErr; // Only fail if truly missing
          }
        }

        if (!enrollmentId) {
          throw new Error('Unable to obtain enrollment ID. Verify program assignment to your department.');
        }
        console.log('✅ Using enrollment ID:', enrollmentId);
      }

      // ==================== DUPLICATE PREVENTION ====================
      if (objective.id && recentSaveIds.has(objective.id)) {
        return objective;
      }
      if (objective.id) {
        recentSaveIds.add(objective.id);
        setTimeout(() => recentSaveIds.delete(objective.id), SAVE_ID_TIMEOUT);
        if (recentSaveIds.size > MAX_RECENT_IDS) {
          const firstId = Array.from(recentSaveIds)[0];
          recentSaveIds.delete(firstId);
        }
      }

      // ==================== PREPARE PAYLOAD ====================
      const isUpdate = uiMode === 'edit';
      const positionTeiId = positionTEI.trackedEntityInstance;
      const positionOrgUnit = positionTEI.orgUnit;

      const userRoleId = user?.userCredentials?.userRoles?.[0]?.id;
      const objectiveData = {
        ...objective,
        objectiveRoleAssigned: objective.objectiveRoleAssigned || userRoleId
      };

      const dataValues = Object.entries(objectiveData)
        .filter(([key, value]) => {
          const hasMapping = dataElementMapping[key];
          const hasValue = value !== null && value !== undefined && value !== '';
          return hasMapping && hasValue;
        })
        .map(([key, value]) => ({
          dataElement: dataElementMapping[key],
          value: String(value),
        }));

      // Link to Position TEI
      dataValues.push({
        dataElement: 'SlgO60cMK3F',
        value: positionTeiId
      });

      const userOrgUnitId = user?.organisationUnits?.[0]?.id;
      if (!userOrgUnitId) {
        throw new Error('User org unit is required to save objective');
      }

      const eventData = {
        program: PROGRAM_ID,
        programStage: PROGRAM_STAGE_ID,
        orgUnit: positionOrgUnit,
        trackedEntityInstance: positionTeiId,
        enrollment: enrollmentId,
        eventDate: objective.startDate || new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        dataValues,
      };

      // For updates, include event ID
      if (isUpdate && objective.id) {
        eventData.event = objective.id;
      }

      // ==================== SAVE TO database ====================
      const url = isUpdate && objective.id ? `/api/events/${objective.id}` : '/api/events';
      const method = isUpdate && objective.id ? 'PUT' : 'POST';
      const apiResponse = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify(eventData),
        credentials: 'include',
      });

      if (!apiResponse.ok) {
        const errorText = await apiResponse.text();
        console.error('❌ API ERROR:', {
          status: apiResponse.status,
          statusText: apiResponse.statusText,
          errorText,
          payload: eventData,
        });
        throw new Error(`HTTP ${apiResponse.status}: ${errorText}`);
      }

      const responseText = await apiResponse.text();
      let parsedResponse = null;
      if (responseText) {
        try {
          parsedResponse = JSON.parse(responseText);
        } catch (parseError) {
          console.warn('âš ï¸ [API DEBUG] Response is not valid JSON:', parseError, responseText);
        }
      }

      const locationHeader = apiResponse.headers.get('Location') || apiResponse.headers.get('location');
      const locationMatch = locationHeader?.match(/events\/([A-Za-z0-9]+)/);
      const locationEventId = locationMatch?.[1];

      const textMatch = responseText?.match(/[A-Za-z0-9]{11,}/);
      const newEventId = extractEventIdFromResponse(parsedResponse || {}, isUpdate, objective.id) ||
        locationEventId ||
        textMatch?.[0];

      if (!newEventId) {
        console.error('âš ï¸ [API DEBUG] Event ID missing after create:', {
          response: parsedResponse,
          responseText,
          locationHeader,
        });
        throw new Error('Failed to receive event ID from server response.');
      }

      const updatedObjective = {
        ...objective,
        id: newEventId,
        orgUnit: positionOrgUnit,
        trackedEntityInstance: positionTeiId,
        enrollment: enrollmentId,
      };

      // ==================== UPDATE CONTEXT ====================
      setData((oldData) => {
        const allObjectives = oldData.flatMap(level => level.objectives);
        if (allObjectives.some(obj => obj.id === newEventId)) return oldData;
        return oldData.map((level) => {
          if (level.level !== editingLevelId) return level;
          const updated = isUpdate
            ? level.objectives.map(o => o.id === newEventId ? updatedObjective : o)
            : [...level.objectives, updatedObjective];
          return { ...level, objectives: updated };
        });
      });

      // Reset UI
      setUiMode('list');
      setEditingLevelId(null);
      setEditingObjective(null);
      showNotification('success', 'Saved successfully!');
      return updatedObjective;

    } catch (error) {
      console.error('[handleSaveObjective] Save failed:', error);
      showNotification('error', 'Save failed', error.message || String(error));
      throw error;
    } finally {
      setLoading(false);
    }
  };


  // ✅ Define loadEvents HERE
  const loadEvents = useCallback(async (rootOrgUnitId) => {
    if (!rootOrgUnitId) { setEvents([]); return; }
    setLoadingEvents(true);
    try {
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: 'yiT00XKrrHP',
            programStage: 'VIBtpvkpHFR',
            orgUnit: rootOrgUnitId,
            orgUnitMode: 'DESCENDANTS', // ← Critical
            fields: 'event,trackedEntityInstance,orgUnit,eventDate,dataValues[dataElement,value]',
            pageSize: 1000
          }
        }
      });
      const eventsArray = response.events?.events || [];
      setEvents(eventsArray);
    } catch (err) {
      console.error("❌ Error fetching events:", err);
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [engine]);

  // ==================== EFFECTS ====================
  useEffect(() => {
    const loadEventsFromRoot = async () => {
      if (!user?.organisationUnits?.[0]?.id) return;

      try {
        // ✅ CORRECT: Wrap in a named key (e.g., 'orgUnitPath')
        const orgUnitResponse = await engine.query({
          orgUnitPath: {  // ← any name works, but must be an object
            resource: `organisationUnits/${user.organisationUnits[0].id}`,
            params: { fields: 'path' }
          }
        });

        // Access via the same name: orgUnitResponse.orgUnitPath
        const path = orgUnitResponse.orgUnitPath.path;
        const rootOrgUnitId = path.split('/').filter(Boolean)[0];

        await loadEvents(rootOrgUnitId);
      } catch (error) {
        console.error('Failed to load events from root:', error);
      }
    };

    if (user?.id) {
      loadEventsFromRoot();
    }
  }, [user?.id, engine, loadEvents]);


  useEffect(() => {
    return () => {
      recentSaveIds.clear();
    };
  }, []);

  // ==================== REFRESH FUNCTION ====================
  const refreshObjectives = useCallback(async () => {
    console.log('🔄 [REFRESH] Refreshing objectives...');
    if (!user?.organisationUnits?.[0]?.id) return;

    try {
      // Get root org unit
      const orgUnitResponse = await engine.query({
        orgUnitPath: {
          resource: `organisationUnits/${user.organisationUnits[0].id}`,
          params: { fields: 'path' }
        }
      });

      const path = orgUnitResponse.orgUnitPath.path;
      const rootOrgUnitId = path.split('/').filter(Boolean)[0];

      // Reload events
      await loadEvents(rootOrgUnitId);
      console.log('✅ [REFRESH] Objectives refreshed successfully');
    } catch (error) {
      console.error('❌ [REFRESH] Failed to refresh objectives:', error);
    }
  }, [user?.organisationUnits?.[0]?.id, engine, loadEvents]);

  // ==================== CONTEXT VALUE ====================
  const value = {
    engine, user, uiMode, setUiMode,
    editingLevelId, setEditingLevelId,
    editingObjective, setEditingObjective,
    selectedHigherLevelObjective, setSelectedHigherLevelObjective,
    selectedGoal, setSelectedGoal,
    handleEditObjective: async (objective) => {
      // Simplified edit handler
      const fullObjective = await fetchObjectiveWithDetails(objective.id, engine);
      if (fullObjective.objective) {
        setEditingObjective(fullObjective.objective);
        setUiMode('edit');
      }
    },
    data, setData, events, setEvents,
    currentOrgUnit, setCurrentOrgUnit,
    loading, setLoading, loadingEvents, setLoadingEvents,
    expandedLevels, setExpandedLevels,
    expandedObjectives, setExpandedObjectives,
    notification, showNotification,
    handleSaveObjective,
    handleUpdateObjective: updateObjective,
    onSaveCallback,
    onErrorCallback,
    loadEvents,
    setEvents,
    refreshObjectives,
    loadHigherLevelObjectiveDetails,
  };

  return (
    <PerformanceContext.Provider
      value={value}>
      {children}
    </PerformanceContext.Provider>
  );
}

const usePerformanceContext = () => {
  const context = useContext(PerformanceContext);
  if (!context) throw new Error('usePerformanceContext must be used within PerformanceProvider');
  return context;
};

export {
  PerformanceContextProvider as PerformanceProvider,
  usePerformanceContext
};
